import React from "react";
import { Box, Grid, IconButton, MenuItem, TextField, Tooltip, Typography } from "@mui/material";
import { Delete as DeleteIcon } from "@mui/icons-material";
import { Formik } from "formik";
import {
  TireChangeDto,
  UpdateVehicleMaintenanceRequest,
  VehicleMaintenanceDto,
  useUpdateVehicleMaintenanceMutation,
} from "../../../api/maintenanceApi";
import { FormDialog } from "../../../components/common/FormDialog/FormDialog";
import {
  GenericNumberField,
  GenericDateField,
  GenericTextField,
  GenericCheckboxField,
} from "../../../components/common/FormFields";
import TireDiagram from "./TireDiagram";

interface MaintenanceEditDialogProps {
  open: boolean;
  onClose: () => void;
  data: VehicleMaintenanceDto | null;
}

const toDateInputValue = (isoDate: string | null | undefined): string => {
  if (!isoDate) return "";
  try {
    return new Date(isoDate).toISOString().split("T")[0];
  } catch {
    return "";
  }
};

interface SectionProps {
  title: string;
  children: React.ReactNode;
  first?: boolean;
}

const Section: React.FC<SectionProps> = ({ title, children, first }) => (
  <Box sx={{ mt: first ? 0 : 3 }}>
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ mb: 1.5, display: "block", letterSpacing: 1 }}
    >
      {title}
    </Typography>
    {children}
  </Box>
);

const TREAD_DEPTH_OPTIONS = Array.from({ length: 19 }, (_, i) => i + 2);
const BRAKE_PAD_OPTIONS = Array.from({ length: 12 }, (_, i) => i + 1);

const TIRE_LABELS: Record<string, string> = {
  LF: "Left Front",
  RF: "Right Front",
  LRO1: "Left Rear Outer 1",
  LRI1: "Left Rear Inner 1",
  RRI1: "Right Rear Inner 1",
  RRO1: "Right Rear Outer 1",
  LRO2: "Left Rear Outer 2",
  LRI2: "Left Rear Inner 2",
  RRI2: "Right Rear Inner 2",
  RRO2: "Right Rear Outer 2",
};

const MaintenanceEditDialog: React.FC<MaintenanceEditDialogProps> = ({
  open,
  onClose,
  data,
}) => {
  const [updateMaintenance, { isLoading }] =
    useUpdateVehicleMaintenanceMutation();

  const initialValues: UpdateVehicleMaintenanceRequest = {
    lastOilChange: data?.lastOilChange ?? null,
    oilChangeThreshold: data?.oilChangeThreshold ?? null,
    lastOilChangeDate: toDateInputValue(data?.lastOilChangeDate) || null,
    alignmentDate: toDateInputValue(data?.alignmentDate) || null,
    tiresChanged: data?.tiresChanged ?? [],
    lastTuneUp: data?.lastTuneUp ?? null,
    tuneUpDate: toDateInputValue(data?.tuneUpDate) || null,
    dpfFilterClean: data?.dpfFilterClean ?? null,
    dpfFilterCleanDate: toDateInputValue(data?.dpfFilterCleanDate) || null,
    isDpfRemoved: data?.isDpfRemoved ?? false,
    defChange: data?.defChange ?? null,
    airDryFilterChange: data?.airDryFilterChange ?? null,
    airDryFilterChangeDate:
      toDateInputValue(data?.airDryFilterChangeDate) || null,
    winterDate: toDateInputValue(data?.winterDate) || null,
    motorkote: data?.motorkote ?? null,
    lastCoolantChange: data?.lastCoolantChange ?? null,
    lastCoolantChangeDate:
      toDateInputValue(data?.lastCoolantChangeDate) || null,
    emissionExpDate: toDateInputValue(data?.emissionExpDate) || null,
  };

  const handleSubmit = async (values: UpdateVehicleMaintenanceRequest) => {
    if (!data) return;
    try {
      await updateMaintenance({
        vehicleId: data.vehicleId,
        data: values,
      }).unwrap();
      onClose();
    } catch (err) {
      console.error("Failed to update maintenance:", err);
    }
  };

  const title = data
    ? `Edit Maintenance — ${data.vehicleName}${
        data.currentOdometer != null
          ? ` (${data.currentOdometer.toLocaleString()} mi)`
          : ""
      }`
    : "Edit Maintenance";

  const handleTireToggle = (
    positionId: string,
    currentTires: TireChangeDto[],
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const exists = currentTires.find((t) => t.position === positionId);
    if (exists) {
      setFieldValue(
        "tiresChanged",
        currentTires.filter((t) => t.position !== positionId)
      );
    } else {
      setFieldValue("tiresChanged", [
        ...currentTires,
        { position: positionId, date: null, note: null, treadDepth32nds: null, brakePadMm: null },
      ]);
    }
  };

  const handleTireFieldChange = (
    index: number,
    field: keyof TireChangeDto,
    value: string | number | null,
    currentTires: TireChangeDto[],
    setFieldValue: (field: string, value: unknown) => void
  ) => {
    const updated = [...currentTires];
    updated[index] = { ...updated[index], [field]: value === "" ? null : value };
    setFieldValue("tiresChanged", updated);
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      onSubmit={handleSubmit}
    >
      {({ handleSubmit: formikSubmit, values, setFieldValue }) => (
        <FormDialog
          open={open}
          title={title}
          onClose={onClose}
          onSubmit={formikSubmit}
          isLoading={isLoading}
          maxWidth="md"
        >
          <Section title="Oil Change" first>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <GenericNumberField
                  name="lastOilChange"
                  label="Last Oil Change (miles)"
                  min={0}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GenericNumberField
                  name="oilChangeThreshold"
                  label="Oil Interval (miles)"
                  min={0}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <GenericDateField
                  name="lastOilChangeDate"
                  label="Last Oil Change Date"
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Alignment & Tires">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericDateField name="alignmentDate" label="Alignment Date" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: "flex", justifyContent: "center" }}>
                  <TireDiagram
                    tires={values.tiresChanged ?? []}
                    onToggle={(id) =>
                      handleTireToggle(id, values.tiresChanged ?? [], setFieldValue)
                    }
                  />
                </Box>
              </Grid>
            </Grid>

            {(values.tiresChanged ?? []).length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 1, display: "block" }}
                >
                  Tire details (click a tire on the diagram to add/remove)
                </Typography>
                {(values.tiresChanged ?? []).map((tire, idx) => (
                  <Box
                    key={tire.position}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ minWidth: 130, fontWeight: 500, fontSize: "0.8125rem" }}
                    >
                      {TIRE_LABELS[tire.position] || tire.position}
                    </Typography>
                    <TextField
                      size="small"
                      type="date"
                      label="Date Changed"
                      InputLabelProps={{ shrink: true }}
                      value={tire.date ? toDateInputValue(tire.date) : ""}
                      onChange={(e) =>
                        handleTireFieldChange(idx, "date", e.target.value, values.tiresChanged ?? [], setFieldValue)
                      }
                      sx={{ width: 155 }}
                    />
                    <TextField
                      select
                      size="small"
                      label="Tread (32nds)"
                      value={tire.treadDepth32nds ?? ""}
                      onChange={(e) =>
                        handleTireFieldChange(
                          idx,
                          "treadDepth32nds",
                          e.target.value === "" ? null : Number(e.target.value),
                          values.tiresChanged ?? [],
                          setFieldValue
                        )
                      }
                      sx={{ width: 120 }}
                    >
                      <MenuItem value="">-</MenuItem>
                      {TREAD_DEPTH_OPTIONS.map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}/32"
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      select
                      size="small"
                      label="Brake Pad (mm)"
                      value={tire.brakePadMm ?? ""}
                      onChange={(e) =>
                        handleTireFieldChange(
                          idx,
                          "brakePadMm",
                          e.target.value === "" ? null : Number(e.target.value),
                          values.tiresChanged ?? [],
                          setFieldValue
                        )
                      }
                      sx={{ width: 130 }}
                    >
                      <MenuItem value="">-</MenuItem>
                      {BRAKE_PAD_OPTIONS.map((v) => (
                        <MenuItem key={v} value={v}>
                          {v}mm
                        </MenuItem>
                      ))}
                    </TextField>
                    <TextField
                      size="small"
                      label="Note"
                      value={tire.note ?? ""}
                      onChange={(e) =>
                        handleTireFieldChange(idx, "note", e.target.value, values.tiresChanged ?? [], setFieldValue)
                      }
                      sx={{ flex: 1, minWidth: 100 }}
                    />
                    <Tooltip title="Remove tire">
                      <IconButton
                        size="small"
                        onClick={() =>
                          setFieldValue(
                            "tiresChanged",
                            (values.tiresChanged ?? []).filter((t) => t.position !== tire.position)
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            )}
          </Section>

          <Section title="Tune Up">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericNumberField
                  name="lastTuneUp"
                  label="Last Tune Up (miles)"
                  min={0}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericDateField name="tuneUpDate" label="Tune Up Date" />
              </Grid>
            </Grid>
          </Section>

          <Section title="DPF Filter">
            <GenericCheckboxField
              name="isDpfRemoved"
              label="DPF Deleted (skip DPF calculations)"
            />
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericNumberField
                  name="dpfFilterClean"
                  label="DPF Filter Clean (miles)"
                  min={0}
                  disabled={values.isDpfRemoved}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericDateField
                  name="dpfFilterCleanDate"
                  label="DPF Filter Clean Date"
                  disabled={values.isDpfRemoved}
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="DEF Change (AdBlue)">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <GenericTextField name="defChange" label="DEF Change" />
              </Grid>
            </Grid>
          </Section>

          <Section title="Air Dry Filter">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericNumberField
                  name="airDryFilterChange"
                  label="Air Dry Filter Change (miles)"
                  min={0}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericDateField
                  name="airDryFilterChangeDate"
                  label="Air Dry Filter Change Date"
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Winter & MOTORKOTE">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericDateField name="winterDate" label="Winter Date" />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericTextField name="motorkote" label="MOTORKOTE" />
              </Grid>
            </Grid>
          </Section>

          <Section title="Coolant">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <GenericNumberField
                  name="lastCoolantChange"
                  label="Last Coolant Change (miles)"
                  min={0}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericDateField
                  name="lastCoolantChangeDate"
                  label="Last Coolant Change Date"
                />
              </Grid>
            </Grid>
          </Section>

          <Section title="Emission">
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <GenericDateField
                  name="emissionExpDate"
                  label="Emission Expiration Date"
                />
              </Grid>
            </Grid>
          </Section>
        </FormDialog>
      )}
    </Formik>
  );
};

export default MaintenanceEditDialog;
